import React from 'react';
import { ShieldQuestion } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Separator } from '../Separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { geoConfig } from '../../data/geoConfig';
import type { TrafficSummary } from '../../utils/traffic';
import type { VpnNetwork, VpnRoute } from '../../types/traffic';
interface NetworkIntegrityCardProps {
  summary: TrafficSummary;
  networks: VpnNetwork[];
  routes: VpnRoute[];
}

/** Renders a signed minute offset as hours, e.g. -330 → '−5h30'. */
function formatClockGap(minutes: number | null): string {
  // Guards undefined and NaN too: rows written before this column existed have no
  // value at all, and a bare null check would let those through as NaN.
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return 'unknown';
  if (minutes === 0) return 'aligned';
  const sign = minutes < 0 ? '−' : '+';
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const rest = absolute % 60;
  return rest === 0 ? `${sign}${hours}h` : `${sign}${hours}h${String(rest).padStart(2, '0')}`;
}
const classLabel: Record<VpnNetwork['networkClass'], string> = {
  vpn: 'VPN',
  hosting: 'Hosting / proxy',
  business: 'Corporate',
  isp: 'ISP',
  unknown: 'Unknown'
};
export function NetworkIntegrityCard({
  summary,
  networks,
  routes
}: NetworkIntegrityCardProps) {
  const hasSignal = summary.vpnResolvedVisitors > 0;
  const isHeuristic = geoConfig.vpnField === null;

  // Coverage must be derived from the SAME numbers shown beside it — the payload's
  // geography coverage is view-based, which contradicted these visitor counts.
  const coverage = summary.uniqueVisitors > 0 ? summary.vpnResolvedVisitors / summary.uniqueVisitors : 0;
  const coverageLabel = coverage > 0 && coverage < 0.01 ? '<1' : String(Math.round(coverage * 100));
  const isLowSample = summary.vpnResolvedVisitors < 30 || coverage < 0.5;
  return <Card data-id="element-203" data-mp="3d31vxgx6fdzg">
      <CardHeader data-id="element-204" data-mp="3d3bpfehfk39w">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-205" data-mp="3hweizmm7jcq3">
          <CardTitle className="flex items-center gap-2" data-id="element-206" data-mp="3hxpj9cgg22cg">
            <ShieldQuestion className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-207" data-mp="345rn027u00xp" />
            VPN / proxy traffic
          </CardTitle>
          <Badge variant="outline" data-id="element-208" data-mp="nrjp0d80jqf">{isHeuristic ? 'Heuristic' : 'Provider flag'}</Badge>
        </div>
        <CardDescription data-id="element-209" data-mp="4pcqc3z5w1s6">
          Which networks tunnelled traffic arrives on, and where it exits
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-210" data-mp="3mu69qdb48ssp">
        {hasSignal ? <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-211" data-mp="3rtsm6kj4e50g">
              <p className="font-mono text-2xl tabular-nums text-foreground" data-id="element-212" data-mp="3rvvn3zlt9ijy">
                {Math.round(summary.vpnSuspectedShare * 100)}%
              </p>
              {isLowSample && <Badge variant="outline" data-id="element-213" data-mp="2gggqiwxikspd">Low sample</Badge>}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-id="element-214" data-mp="28ubjzhkzi9cx">
              Based on {summary.vpnResolvedVisitors.toLocaleString()} of{' '}
              {summary.uniqueVisitors.toLocaleString()} visitors ({coverageLabel}% geo coverage) —
              the rest never resolved, so this is not a rate for all traffic.
            </p>
            {isLowSample && <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-id="element-215" data-mp="1p6vrjojzfsm2">
                Coverage is low because the lookup was added recently, is cached per session, and
                free provider tiers cap near 1k lookups/day. Resolve geography at the edge for full
                coverage.
              </p>}
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-id="element-216" data-mp="1u4x9g65o9lvy">
              Networks are identified by the ASN that owns the IP — many VPN brands resell the same
              hosts, so labels are strong hints, not proof. Datacenter exits (e.g. Ashburn US with a
              UTC browser time zone) are usually bots or monitors rather than people.
            </p>

            {networks.length > 0 && <>
                <Separator className="my-5" data-id="element-217" data-mp="1p2pplpx2t34t" />
                <h3 className="text-sm font-medium text-foreground" data-id="element-218" data-mp="1p1xro8g0atbd">Networks</h3>
                <div className="-mx-1 mt-2 overflow-x-auto px-1" data-id="element-219" data-mp="1tto4cl2f1t5y">
                  <Table data-id="element-220" data-mp="1xv1dp54pwehm">
                    <TableHeader data-id="element-221" data-mp="1xvsx7hl1ilrj">
                      <TableRow data-id="element-222" data-mp="1xuhrhc81b3xs">
                        <TableHead data-id="element-223" data-mp="22u46nr7fau98">Operator</TableHead>
                        <TableHead data-id="element-224" data-mp="22st1po28rggj">Type</TableHead>
                        <TableHead className="text-right" data-id="element-225" data-mp="27t7ax9fkwwg5">ASN</TableHead>
                        <TableHead className="text-right" data-id="element-226" data-mp="27r4d4386pz0v">Visitors</TableHead>
                        <TableHead className="text-right" data-id="element-227" data-mp="2crs3cmolabu1">Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="element-228" data-mp="ie9ahq5i7hf2">
                      {networks.map(network => <TableRow key={`${network.label}-${network.asn ?? 'na'}`} data-id="element-229" data-mp="igdes97beifv">
                          <TableCell className="text-xs" data-id="element-230" data-mp="2vptvmsqrgy43">{network.label}</TableCell>
                          <TableCell data-id="element-231" data-mp="2qx0ueeui47wr">
                            <Badge variant="secondary" data-id="element-232" data-mp="2qvzik85nvhdz">{classLabel[network.networkClass]}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs" data-id="element-233" data-mp="2hxx7t0w4ladi">
                            {network.asn ?? '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-234" data-mp="3altduw7l4emy">
                            {network.visitors.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-235" data-mp="35ojhsxpox4q5">
                            {Math.round(network.share * 100)}%
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </div>
              </>}

            {routes.length > 0 && <>
                <Separator className="my-5" data-id="element-236" data-mp="1vogpbiji550f" />
                <h3 className="text-sm font-medium text-foreground" data-id="element-237" data-mp="1vnf9z1vgdwcw">Exit location vs visitor</h3>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-238" data-mp="1ho5xodadbme7">
                  Where the connection surfaces, next to the visitor&rsquo;s own clock. Clock gap is
                  how far apart the two are — the bigger the gap, the further the traffic is routed.
                </p>
                <div className="-mx-1 mt-2 overflow-x-auto px-1" data-id="element-239" data-mp="1cqw90zuszerf">
                  <Table data-id="element-240" data-mp="yypwrourtrx9">
                    <TableHeader data-id="element-241" data-mp="yzhga1b3fz76">
                      <TableRow data-id="element-242" data-mp="z0an12a1yopb">
                        <TableHead data-id="element-243" data-mp="12zrwe6slo5zr">Exit location</TableHead>
                        <TableHead data-id="element-244" data-mp="130b1uo12d1ww">Visitor time zone</TableHead>
                        <TableHead className="text-right" data-id="element-245" data-mp="p00e3tv1w3fy">Clock gap</TableHead>
                        <TableHead className="text-right" data-id="element-246" data-mp="tz3f937wtpin">Visitors</TableHead>
                        <TableHead className="text-right" data-id="element-247" data-mp="u17mnv50p6no">Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="element-248" data-mp="5f9s0x45z8tc">
                      {routes.map(route => <TableRow key={`${route.exitCountry}-${route.exitCity}-${route.browserTimeZone}`} data-id="element-249" data-mp="kcacqgnxu3ib">
                          <TableCell className="whitespace-nowrap font-mono text-xs" data-id="element-250" data-mp="feqznk2z7wry">
                            {route.exitCity}, {route.exitCountry}
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-mono text-xs" data-id="element-251" data-mp="od2mdb0q2af4">
                            {route.browserTimeZone}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-mono text-xs tabular-nums" data-id="element-252" data-mp="y474gjlp2be6">
                            {formatClockGap(route.clockGapMinutes)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-253" data-mp="182wn4elezzvh">
                            {route.visitors.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-254" data-mp="2je2zqa21gmu2">
                            {Math.round(route.share * 100)}%
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </div>
              </>}
          </> : <div className="py-2 text-xs leading-relaxed text-muted-foreground" data-id="element-255" data-mp="20pxyb98utdyf">
            <p className="font-medium text-foreground" data-id="element-256" data-mp="24pybyro4pl4f">No network signal yet</p>
            <p className="mt-1.5" data-id="element-257" data-mp="29h6kllhmx2gj">The lookup is configured, so this is almost always one of:</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4" data-id="element-258" data-mp="29j154yozusk3">
              <li data-id="element-259" data-mp="1cysigu2wylo4">
                <span className="font-mono" data-id="element-260" data-mp="1cx7ph8w8zasd">supabase/schema.sql</span> hasn&rsquo;t been re-run since
                the network columns were added
              </li>
              <li data-id="element-261" data-mp="19g35u3b2joko">the geo provider is rate-limited — free tiers cap around 1k lookups/day</li>
              <li data-id="element-262" data-mp="14jcsvouk993h">no new page views since the change; existing rows predate it</li>
            </ul>
          </div>}
      </CardContent>
    </Card>;
}